import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { ChangePasswordModal } from './ChangePasswordModal'

interface NavItem {
  to: string
  label: string
  icon: JSX.Element
  end?: boolean
}

interface NavGroup {
  label: string
  adminOnly?: boolean
  items: NavItem[]
}

const I = (d: string) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
    <path d={d} />
  </svg>
)

const DASHBOARD: NavItem = { to: '/', label: 'Dashboard', end: true, icon: I('M3 12l9-9 9 9M5 10v10h14V10') }
const PLACAR: NavItem = { to: '/placar', label: 'Placar', icon: I('M8 21h8M12 17v4M7 4h10v3a5 5 0 01-10 0zM7 5H4v1a3 3 0 003 3M17 5h3v1a3 3 0 01-3 3') }

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Clientes',
    items: [
      { to: '/clients', label: 'Lista', icon: I('M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75') },
      { to: '/reports', label: 'Relatórios', icon: I('M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8') },
      { to: '/clients/chat', label: 'Falar sobre', icon: I('M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z') },
      { to: '/intelligence', label: 'Inteligência da IA', icon: I('M9.5 2a3.5 3.5 0 00-3.4 4.3A3.5 3.5 0 004 12a3.5 3.5 0 002.6 3.4A3.5 3.5 0 0012 18.5V2.6A3.5 3.5 0 009.5 2zM14.5 2a3.5 3.5 0 013.4 4.3A3.5 3.5 0 0120 12a3.5 3.5 0 01-2.6 3.4A3.5 3.5 0 0112 18.5M12 18.5V22') },
    ],
  },
  {
    label: 'Ações',
    items: [
      { to: '/open-actions', label: 'Ações abertas', icon: I('M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11') },
      { to: '/open-actions-by-week', label: 'Ações por semana', icon: I('M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z') },
      { to: '/open-analyses', label: 'Análises', icon: I('M11 4a7 7 0 100 14 7 7 0 000-14zM21 21l-4.35-4.35') },
      { to: '/impact', label: 'Impacto', icon: I('M12 15a7 7 0 100-14 7 7 0 000 14zM8.21 13.89L7 23l5-3 5 3-1.21-9.12') },
    ],
  },
  {
    label: 'Relatórios',
    items: [
      { to: '/planning', label: 'Planejamento Semanal', icon: I('M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2zM8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01') },
      { to: '/trends', label: 'Tendências', icon: I('M3 3v18h18M7 14l3-3 3 3 5-5') },
    ],
  },
  {
    label: 'Admin',
    adminOnly: true,
    items: [
      { to: '/users', label: 'Usuários', icon: I('M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75') },
      { to: '/manager-activity', label: 'Atividade', icon: I('M3 3v18h18M7 14l3-3 3 3 5-5') },
      { to: '/report-errors', label: 'Erros de geração', icon: I('M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z') },
      { to: '/settings', label: 'Configurações', icon: I('M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-2.81 1.17V21a2 2 0 11-4 0v-.09A1.65 1.65 0 008 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 004.6 14a1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 8a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 008 4.6a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 8c.14.31.22.65.22 1z') },
    ],
  },
]

function NavRow({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium whitespace-nowrap transition-all ${
          isActive
            ? 'bg-white/10 text-white shadow-glow-sm'
            : 'text-slate-400 hover:bg-white/5 hover:text-slate-100'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-brand-gradient transition-opacity ${
              isActive ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <span className={isActive ? 'text-brand-300' : 'text-slate-500 group-hover:text-slate-300'}>
            {item.icon}
          </span>
          {item.label}
        </>
      )}
    </NavLink>
  )
}

export default function Layout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [showPwd, setShowPwd] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  const groups = NAV_GROUPS.filter((g) => !g.adminOnly || user?.role === 'admin')
  const initials = (user?.name ?? '?')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div className="flex h-screen overflow-hidden print:block print:h-auto print:overflow-visible">
      {/* Backdrop do drawer (mobile) */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 transform overflow-y-auto bg-ink-gradient text-slate-100 transition-transform duration-200 ease-out md:static md:z-auto md:translate-x-0 md:flex-shrink-0 md:overflow-visible print:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="pointer-events-none absolute inset-0 bg-grid [background-size:28px_28px] opacity-40" />
        <button
          onClick={() => setMobileOpen(false)}
          aria-label="Fechar menu"
          className="absolute right-3 top-4 z-10 rounded-lg p-1.5 text-slate-300 hover:bg-white/10 md:hidden"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-5 w-5">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <div className="relative flex h-full flex-col">
          {/* Marca */}
          <div className="flex shrink-0 items-center gap-3 px-5 py-5">
            <div className="relative">
              <div className="absolute -inset-1 rounded-2xl bg-brand-gradient opacity-50 blur-md" />
              <img
                src="/img/Logo_960px_3D.webp"
                alt="Lucro Digital"
                className="relative h-11 w-11 rounded-2xl object-contain"
              />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold text-white">Lucro Digital</div>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-brand-300">
                Analytics
                <span className="rounded bg-white/10 px-1 py-px text-[9px] font-bold tracking-wider text-brand-200">
                  AI
                </span>
              </div>
            </div>
          </div>

          <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3 md:mt-2">
            <NavRow item={DASHBOARD} onNavigate={() => setMobileOpen(false)} />
            <NavRow item={PLACAR} onNavigate={() => setMobileOpen(false)} />
            {groups.map((g) => (
              <div key={g.label} className="mt-3">
                <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  {g.label}
                </div>
                {g.items.map((item) => (
                  <NavRow key={item.to} item={item} onNavigate={() => setMobileOpen(false)} />
                ))}
              </div>
            ))}
          </nav>

          {/* Usuário */}
          <div className="shrink-0 border-t border-white/10 p-3">
            <div className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-gradient text-xs font-bold text-white">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-white">{user?.name}</div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">
                  {user?.role === 'admin' ? 'Administrador' : 'Gerente'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200/70 bg-white/70 px-4 py-3 backdrop-blur-md md:px-6 print:hidden">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileOpen(true)}
              aria-label="Abrir menu"
              className="-ml-1 rounded-lg p-1.5 text-slate-600 hover:bg-slate-100 md:hidden"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-6 w-6">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="text-sm font-medium text-slate-500">Lucro Digital Analytics</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm font-medium text-slate-700 sm:inline">{user?.name}</span>
            <button
              onClick={() => setShowPwd(true)}
              className="rounded-lg px-2.5 py-1.5 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
            >
              Senha
            </button>
            <button
              onClick={handleLogout}
              className="rounded-lg px-2.5 py-1.5 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
            >
              Sair
            </button>
          </div>
        </header>

        {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}

        <main className="flex-1 overflow-y-auto print:overflow-visible">
          <div className="p-4 md:p-6 max-w-6xl w-full mx-auto animate-fade-up">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
